// Reading a bank statement PDF (Liam 2026-09-27: "add pdf reading to it").
// Many banks give a PDF statement rather than a spreadsheet. The PDF's text
// is read with pdf.js (loaded only on this screen), laid back out into lines
// by position, and turned into the same rows a spreadsheet download gives
// (Date, Description, Money in, Money out), which bank-statement.ts then
// reads like any other file. Built and first checked on a Barclays business
// statement (Dropby, August 2026). No AI: fixed rules.
//
// The half that turns positioned text into rows is pure (statementRowsFromPdf)
// so it can be tested without a browser.

export interface PdfItem {
  str: string
  // Left edge and baseline, in PDF points (y grows upwards).
  x: number
  y: number
  w: number
  h: number
}

export interface PdfPage {
  items: PdfItem[]
}

export interface PdfRead {
  rows: string[][]
  // The money-in total the statement itself prints, when it prints one.
  statedIn: number | null
  // The money-in total read from the lines.
  readIn: number
  problem: string | null
}

// pdf.js, from the app's own files (never a CDN), only when a PDF is chosen.
export async function pdfPages(data: ArrayBuffer): Promise<PdfPage[]> {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
  const worker = (await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = worker
  const doc = await pdfjs.getDocument({ data: new Uint8Array(data), isEvalSupported: false }).promise
  const pages: PdfPage[] = []
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n)
    const content = await page.getTextContent()
    const items: PdfItem[] = []
    for (const it of content.items as { str?: string; transform?: number[]; width?: number; height?: number }[]) {
      if (typeof it.str !== 'string' || !it.str.trim() || !it.transform) continue
      items.push({ str: it.str, x: it.transform[4], y: it.transform[5], w: it.width ?? 0, h: it.height || Math.abs(it.transform[3]) || 8 })
    }
    pages.push({ items })
  }
  await doc.destroy()
  return pages
}

type Col = 'in' | 'out' | 'amount' | 'balance'

interface Line {
  y: number
  h: number
  items: PdfItem[]
}

const MONEY = /^\(?-?£?\s?\d{1,3}(,\d{3})*(\.\d{2})\)?(\s?(CR|DR))?$|^\(?-?£?\s?\d+\.\d{2}\)?(\s?(CR|DR))?$/i
const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 }
const SUMMARY = /\b(total|brought forward|carried forward|start balance|end balance|opening balance|closing balance|balance brought|balance carried|continued)\b/i
const HEAD: [Col | 'date' | 'desc', RegExp][] = [
  ['date', /^date$/],
  ['in', /^(money in|paid in|credits?|in|receipts|deposits|payments in)$/],
  ['out', /^(money out|paid out|debits?|out|withdrawn|withdrawals|payments out)$/],
  ['amount', /^(amount|value)$/],
  ['balance', /^balance$/],
  ['desc', /^(description|details|transaction details|payment type and details|particulars|narrative|transaction)$/],
]

function linesOf(items: PdfItem[]): Line[] {
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x)
  const lines: Line[] = []
  for (const it of sorted) {
    const last = lines[lines.length - 1]
    if (last && Math.abs(last.y - it.y) <= Math.max(2, Math.min(last.h, it.h) * 0.45)) last.items.push(it)
    else lines.push({ y: it.y, h: it.h, items: [it] })
  }
  for (const l of lines) l.items.sort((a, b) => a.x - b.x)
  return lines
}

// Items close together make one phrase ("Money" "out £" -> "Money out £").
function phrases(items: PdfItem[]): PdfItem[] {
  const out: PdfItem[] = []
  for (const it of items) {
    const last = out[out.length - 1]
    const gap = last ? it.x - (last.x + last.w) : Infinity
    if (last && gap < Math.max(4, it.h * 0.6)) {
      out[out.length - 1] = { str: `${last.str}${gap > 0.5 ? ' ' : ''}${it.str}`, x: last.x, y: last.y, w: it.x + it.w - last.x, h: Math.max(last.h, it.h) }
    } else out.push({ ...it })
  }
  return out
}

const norm = (s: string): string => s.toLowerCase().replace(/£|\(gbp\)|gbp/g, ' ').replace(/[():]/g, ' ').replace(/\s+/g, ' ').trim()

interface Header {
  cols: { col: Col; left: number; right: number }[]
  descLeft: number
  tableRight: number
}

// Headings can wrap onto two lines ("Money" over "out £"): the items of the
// lines just above and below a line holding "Date" are stacked by column
// into one heading each.
function headerOf(lines: Line[], at: number): Header | null {
  const line = lines[at]
  if (!line.items.some((i) => /^date$/i.test(i.str.trim()) || /^date\b/i.test(i.str.trim()))) return null
  const band = lines.filter((l) => Math.abs(l.y - line.y) <= line.h * 1.6).flatMap((l) => l.items)
  const stacked: PdfItem[] = []
  for (const it of [...band].sort((a, b) => b.y - a.y)) {
    const col = stacked.find((c) => it.x < c.x + c.w && c.x < it.x + it.w && Math.abs(c.y - it.y) <= line.h * 1.6)
    if (col) {
      const left = Math.min(col.x, it.x)
      const right = Math.max(col.x + col.w, it.x + it.w)
      col.str = `${col.str} ${it.str}`
      col.x = left
      col.w = right - left
    } else stacked.push({ ...it })
  }
  // Side by side pieces join only when together they make a heading ("Money"
  // + "in £"); two headings that touch ("Money in £" | "Balance £") stay two.
  const roleOf = (str: string): Col | 'date' | 'desc' | null => HEAD.find(([, re]) => re.test(norm(str)))?.[0] ?? null
  const sorted = stacked.sort((a, b) => a.x - b.x)
  const ps: PdfItem[] = []
  for (let i = 0; i < sorted.length; i++) {
    const it = sorted[i]
    const next = sorted[i + 1]
    if (!roleOf(it.str) && next && next.x - (it.x + it.w) < Math.max(6, it.h) && roleOf(`${it.str} ${next.str}`)) {
      ps.push({ str: `${it.str} ${next.str}`, x: it.x, y: it.y, w: next.x + next.w - it.x, h: it.h })
      i++
    } else ps.push(it)
  }
  const roles = ps.map((p) => roleOf(p.str))
  if (!roles.includes('date')) return null
  const cols = ps
    .map((p, i) => ({ p, r: roles[i] }))
    .filter((x): x is { p: PdfItem; r: Col } => x.r === 'in' || x.r === 'out' || x.r === 'amount' || x.r === 'balance')
    .map(({ p, r }) => ({ col: r, left: p.x, right: p.x + p.w }))
  if (!cols.some((c) => c.col === 'in' || c.col === 'amount')) return null
  const descAt = roles.indexOf('desc')
  const dateAt = roles.indexOf('date')
  const descLeft = descAt >= 0 ? ps[descAt].x : ps[dateAt].x + ps[dateAt].w + 2
  const tableRight = Math.max(...cols.map((c) => c.right)) + 20
  return { cols, descLeft, tableRight }
}

function money(s: string): number | null {
  let t = s.replace(/[£\s,]/g, '')
  let sign = 1
  if (/^\(.*\)$/.test(t)) { sign = -1; t = t.slice(1, -1) }
  if (/CR$/i.test(t)) t = t.slice(0, -2)
  else if (/DR$/i.test(t)) { sign = -sign; t = t.slice(0, -2) }
  if (t.startsWith('-')) { sign = -sign; t = t.slice(1) }
  return /^\d+(\.\d{2})$/.test(t) ? sign * Math.round(Number(t) * 100) : null
}

// A date at the start of a table line: "6 Aug", "06 Aug 26", "06 Aug 2026",
// "06/08/2026", "06/08/26", "2026-08-06". Returns the date and how many
// characters it took, with the year still unknown for "6 Aug".
function leadingDate(text: string): { y: number | null; m: number; d: number; len: number } | null {
  let m = text.match(/^(\d{1,2})\s?([A-Za-z]{3})[a-z]*\.?(?:\s?(\d{2,4}))?\b/)
  if (m && MONTHS[m[2].toLowerCase()]) {
    const y = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : null
    return { y, m: MONTHS[m[2].toLowerCase()], d: Number(m[1]), len: m[0].length }
  }
  m = text.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/)
  if (m) return { y: m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]), m: Number(m[2]), d: Number(m[1]), len: m[0].length }
  m = text.match(/^(\d{4})-(\d{2})-(\d{2})\b/)
  if (m) return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), len: m[0].length }
  return null
}

const iso = (y: number, m: number, d: number): string => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`

export function statementRowsFromPdf(pages: PdfPage[]): PdfRead {
  const all = pages.flatMap((p) => linesOf(p.items))
  if (all.length === 0) return { rows: [], statedIn: null, readIn: 0, problem: 'This PDF has no text in it (it may be a scan or a photo). Download the statement from online banking instead.' }
  // The statement's own year and last day, for dates printed without a year:
  // the latest full date anywhere in it ("Statement date 1 Sep 2026").
  let end: string | null = null
  const fullDate = /\b(\d{1,2})\s([A-Za-z]{3})[a-z]*\.?\s(20\d{2})\b/g
  for (const l of all) {
    const text = l.items.map((i) => i.str).join(' ')
    for (const m of text.matchAll(fullDate)) {
      const mo = MONTHS[m[2].toLowerCase()]
      if (!mo) continue
      const d = iso(Number(m[3]), mo, Number(m[1]))
      if (!end || d > end) end = d
    }
  }
  // The money-in total the statement prints, to check the reading against.
  let statedIn: number | null = null
  for (const l of all) {
    const text = phrases(l.items).map((p) => p.str).join(' | ')
    const m = text.match(/\b(money in|paid in|total paid in|total money in|total credits|payments in|total receipts)\b[^|]*\|\s*£?\s?([\d,]+\.\d{2})\b/i) ?? text.match(/\b(money in|total paid in|total money in)\s*£\s?([\d,]+\.\d{2})\b/i)
    if (m) {
      statedIn = money(m[2])
      break
    }
  }

  type Tx = { date: string | null; desc: string[]; pin: number | null; pout: number | null; y: number; h: number; page: number }
  const txs: Tx[] = []
  let header: Header | null = null
  let date: { y: number | null; m: number; d: number } | null = null
  let cur: Tx | null = null
  let lastY = 0
  pages.forEach((page, pageNo) => {
    cur = null
    let inTable = false
    const pageLines = linesOf(page.items)
    let skipUntilY = Infinity
    for (let li = 0; li < pageLines.length; li++) {
      const line = pageLines[li]
      // The rest of a wrapped heading, already read.
      if (line.y >= skipUntilY) continue
      const h = headerOf(pageLines, li)
      if (h) {
        skipUntilY = line.y - line.h * 1.6
        header = h
        inTable = true
        cur = null
        lastY = line.y
        continue
      }
      if (!header || !inTable) continue
      const hd: Header = header
      const items = line.items.filter((i) => i.x < hd.tableRight)
      if (items.length === 0) continue
      const text = items.filter((i) => i.x < hd.cols.reduce((m, c) => Math.min(m, c.left), Infinity) - 4).map((i) => i.str).join(' ').replace(/\s+/g, ' ').trim()
      // Money on this line, by the column it sits under (right edges line up).
      let pin: number | null = null
      let pout: number | null = null
      let amount: number | null = null
      for (const it of items) {
        const s = it.str.trim()
        if (!MONEY.test(s)) continue
        const right = it.x + it.w
        const near = hd.cols.map((c) => ({ c, dist: Math.min(Math.abs(right - c.right), Math.abs(it.x - c.left)) })).sort((a, b) => a.dist - b.dist)[0]
        if (!near || near.dist > 45) continue
        const v = money(s)
        if (v === null) continue
        if (near.c.col === 'in') pin = Math.abs(v)
        else if (near.c.col === 'out') pout = Math.abs(v)
        else if (near.c.col === 'amount') amount = v
      }
      if (amount !== null) {
        if (amount >= 0) pin = amount
        else pout = -amount
      }
      const gap = lastY - line.y
      lastY = line.y
      // A date only counts in the date column: "20 Aug at VISA Exchange
      // Rate" on a card payment's second line is not a new day (Dropby's
      // August statement).
      const inDateColumn = items[0].x < hd.descLeft - 2
      const lead = inDateColumn ? leadingDate(text) : null
      let desc = text
      if (lead) {
        date = { y: lead.y, m: lead.m, d: lead.d }
        desc = text.slice(lead.len).trim()
      }
      if (SUMMARY.test(desc)) {
        cur = null
        continue
      }
      if (pin !== null || pout !== null) {
        let when: string | null = null
        if (date) {
          let y = date.y ?? (end ? Number(end.slice(0, 4)) : null)
          if (y !== null && date.y === null && end && iso(y, date.m, date.d) > end) y -= 1
          when = y !== null ? iso(y, date.m, date.d) : null
        }
        cur = { date: when, desc: desc ? [desc] : [], pin, pout, y: line.y, h: line.h, page: pageNo }
        txs.push(cur)
      } else if (cur && desc && gap <= line.h * 2.4) {
        // A line of words right under a payment: more of its description
        // ("Ref: 23 Huntingdon Rd").
        cur.desc.push(desc)
      } else {
        cur = null
      }
    }
  })
  const rows: string[][] = [['Date', 'Description', 'Money in', 'Money out']]
  let readIn = 0
  for (const t of txs) {
    if (!t.date) continue
    rows.push([t.date, t.desc.join(' '), t.pin !== null ? (t.pin / 100).toFixed(2) : '', t.pout !== null ? (t.pout / 100).toFixed(2) : ''])
    if (t.pin !== null) readIn += t.pin
  }
  if (rows.length === 1) return { rows: [], statedIn, readIn: 0, problem: "Roundly couldn't find the list of payments in this PDF." }
  return { rows, statedIn, readIn, problem: null }
}
